import { SelectProps, Stack } from '@mantine/core';
import IntegrationLogo from '@/pages/Integrations/components/IntegrationLogo';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import React, { useMemo } from 'react';
import useRetrieveIntegrations from '@/hooks/api/integrations/useRetrieveIntegrations.ts';
import { IntegrationInstance, IntegrationProvider } from '@/types/integrations.ts';
import CustomSelectOption from '@/components/CustomSelectOption.tsx';
import {
  isExternalIntegrationProvider,
  isStandaloneCapabilityProvider,
  providerUsesIntegrationInstance,
} from '@/utils/providerKinds.ts';
import { getIntegrationSelectLabel } from '@/utils/integrationDisplay.ts';

type SelectType = 'linked_datasets' | 'trigger' | 'dispatch';

interface SelectConnectionProps {
  onSelect: (p?: IntegrationProvider, i?: IntegrationInstance) => void;
  selectType: SelectType;
  provider?: IntegrationProvider;
  instance?: IntegrationInstance;
  integrationError?: string;
  label?: string;
}

type ReturnType = {
  integration?: IntegrationInstance;
  provider?: IntegrationProvider;
};

const findMatchingIntegrationAndProvider = (
  integrationProviders: IntegrationProvider[],
  value?: string | null,
): ReturnType => {
  if (!value) return { integration: undefined, provider: undefined };
  const parts = value.split(' ');
  const providerName = parts[0];
  const instanceID = parts.length > 1 ? parts[1] : undefined;

  const provider = integrationProviders.find((p) => p.name === providerName);
  const integration = provider?.instances.find((inst) => inst.id === instanceID);
  return { integration, provider };
};

const makeSelectionOption = (provider: IntegrationProvider, instance?: IntegrationInstance) => {
  return { value: makeSelectValue(provider, instance), label: makeSelectLabel(provider, instance) };
};

const makeSelectValue = (provider?: IntegrationProvider, instance?: IntegrationInstance) => {
  if (!provider) return '';
  if (!instance) return provider.name;
  return provider.name + ' ' + instance.id;
};

const makeSelectLabel = (provider?: IntegrationProvider, instance?: IntegrationInstance) => {
  return getIntegrationSelectLabel(provider, instance);
};

export function IntegrationSelect({
  onSelect,
  provider,
  instance,
  selectType,
  integrationError,
  label,
}: SelectConnectionProps) {
  const { data: allIntegrations } = useRetrieveIntegrations();

  const integrationProviders = useMemo(() => {
    return allIntegrations?.results || [];
  }, [allIntegrations?.results]);

  const options = useMemo(() => {
    if (selectType === 'linked_datasets') {
      return integrationProviders
        .filter(
          (p) =>
            isExternalIntegrationProvider(p) &&
            p.instances.length > 0 &&
            p.supports_linked_datasets,
        )
        .flatMap((p) =>
          p.instances.map((i) => {
            return { value: makeSelectValue(p, i), label: makeSelectLabel(p, i) };
          }),
        );
    } else {
      let filteredProviders = integrationProviders.filter(
        (i) => !providerUsesIntegrationInstance(i) || i.instances.length > 0,
      );
      if (selectType === 'trigger') {
        filteredProviders = filteredProviders.filter((i) => i.trigger_actions.length > 0);
      } else {
        filteredProviders = filteredProviders.filter(
          (i) => i.dispatch_actions.length > 0 && !isStandaloneCapabilityProvider(i),
        );
      }
      return filteredProviders.flatMap((p) =>
        providerUsesIntegrationInstance(p)
          ? p.instances.map((i) => makeSelectionOption(p, i))
          : [makeSelectionOption(p)],
      );
    }
  }, [integrationProviders, selectType]);

  const handleChange = (value: string | null) => {
    const match = findMatchingIntegrationAndProvider(integrationProviders, value);
    onSelect(match?.provider, match?.integration);
  };

  const renderOption: SelectProps['renderOption'] = ({ option, checked }) => {
    const match = findMatchingIntegrationAndProvider(integrationProviders, option.value);
    const providerName = match?.provider?.name;
    const leftSection = providerName && <IntegrationLogo name={providerName} />;
    return <CustomSelectOption label={option.label} leftSection={leftSection} checked={checked} />;
  };

  const noIntegrations = options.length === 0;

  return (
    <Stack>
      <StyledSelect
        label={label}
        placeholder={!noIntegrations ? 'Select an integration' : 'No integrations connected'}
        data={options}
        value={makeSelectValue(provider, instance)}
        onChange={handleChange}
        required={true}
        disabled={noIntegrations}
        allowDeselect={false}
        clearable={false}
        error={integrationError}
        renderOption={renderOption}
      />
    </Stack>
  );
}
